import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDay } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { elementToPdf } from "@/lib/pdf";
import { toast } from "sonner";
import { ArrowLeft, Download, Printer, Share2, Wallet, Trash2, Shirt } from "lucide-react";

const selCls = "h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white w-full";
const INV_COLORS = { "Émise": "bg-slate-100 text-slate-700", "Partiellement payée": "bg-amber-100 text-amber-700", "Payée": "bg-emerald-100 text-emerald-700" };
const PAY_MODES = ["Chèque", "Virement", "Espèces", "Mobile Money", "Autre"];
const today = () => new Date().toISOString().slice(0, 10);

export default function ProInvoice() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const docRef = useRef(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(null);

  const load = useCallback(() => {
    api.get(`/pro/invoices/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate-400 p-8">Chargement...</div>;
  const { invoice: inv, orders, payments, client, settings: s, amount_in_words } = data;
  const cur = s?.currency || "KMF";
  const company = s?.company_name || "WuraPro";
  const fileName = `Facture_${company}_${(client?.name || "").replace(/[^a-zA-Z0-9]/g, "")}_${inv.number}.pdf`.replace(/\s/g, "");
  const kg = (n) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const words = amount_in_words ? amount_in_words.charAt(0).toUpperCase() + amount_in_words.slice(1) : "";

  const download = async () => { setBusy(true); try { await elementToPdf(docRef.current, fileName, { width: 800 }); } catch (e) { toast.error("Erreur PDF"); } finally { setBusy(false); } };
  const print = () => window.print();
  const share = async () => {
    setBusy(true);
    try {
      const blob = await elementToPdf(docRef.current, fileName, { width: 800 });
      const file = new File([blob], fileName, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Facture ${inv.number}`, text: `Facture ${inv.number} - ${company}` });
      } else { toast.success("PDF téléchargé (partage non supporté)"); }
    } catch (e) { /* cancelled */ } finally { setBusy(false); }
  };

  const savePayment = async () => {
    const amount = Number(pay.amount);
    if (!amount || amount <= 0) return toast.error("Montant invalide");
    try {
      await api.post(`/pro/invoices/${id}/payment`, { amount, method: pay.method, reference: pay.reference, date: pay.date });
      toast.success("Paiement enregistré"); setPay(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const cancelInvoice = async () => {
    if (!window.confirm("Annuler cette facture ? Les commandes redeviendront disponibles pour une nouvelle facture.")) return;
    try { await api.delete(`/pro/invoices/${id}`); toast.success("Facture annulée"); nav(`/pro/clients/${inv.pro_client_id}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="pro-invoice-page">
      <div className="no-print flex flex-wrap items-center gap-2">
        <button onClick={() => nav(`/pro/clients/${inv.pro_client_id}`)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mr-auto"><ArrowLeft size={16} /> Retour à la fiche</button>
        <Button variant="outline" data-testid="inv-download" onClick={download} disabled={busy}><Download size={16} className="mr-1.5" /> Télécharger</Button>
        <Button variant="outline" data-testid="inv-print" onClick={print}><Printer size={16} className="mr-1.5" /> Imprimer</Button>
        <Button variant="outline" data-testid="inv-share" onClick={share} disabled={busy}><Share2 size={16} className="mr-1.5" /> Partager</Button>
        <Button data-testid="inv-payment" onClick={() => setPay({ amount: inv.balance, method: "Chèque", reference: "", date: today() })} className="bg-emerald-600 hover:bg-emerald-700"><Wallet size={16} className="mr-1.5" /> Enregistrer un paiement</Button>
        {isAdmin && <Button variant="outline" data-testid="inv-cancel" onClick={cancelInvoice} className="text-red-600 border-red-200"><Trash2 size={16} className="mr-1.5" /> Annuler</Button>}
      </div>

      {/* Document facture — mise en page calquée sur le modèle CHN */}
      <div ref={docRef} className="print-area bg-white mx-auto max-w-3xl p-10 rounded-xl border border-slate-200 shadow-sm text-slate-900" data-testid="invoice-document" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
        <div className="flex items-start gap-3 border-b-2 border-slate-800 pb-4">
          {s?.logo_url && <img src={s.logo_url} alt="logo" className="h-16 w-16 rounded object-cover" />}
          <div>
            <p className="text-2xl font-extrabold tracking-tight">{company}</p>
            <p className="text-sm text-slate-600">Blanchisserie Professionnelle</p>
            {s?.address && <p className="text-xs text-slate-500 mt-0.5">{s.address}</p>}
            {s?.whatsapp && <p className="text-xs text-slate-500">Tél. : {s.whatsapp}</p>}
          </div>
        </div>

        <p className="text-center text-xl font-extrabold mt-6 tracking-wide">FACTURE {inv.number}</p>

        <div className="mt-5 text-sm space-y-0.5">
          <p><span className="font-semibold">Date d'émission :</span> {fmtDay(inv.issue_date)}</p>
          <p><span className="font-semibold">Période des prestations :</span> du {fmtDay(inv.period_from)} au {fmtDay(inv.period_to)}</p>
        </div>

        <div className="mt-4 text-sm">
          <p className="font-semibold underline">Client</p>
          <p className="font-bold">{client?.name}</p>
          {client?.phone && <p className="text-slate-600">Tél. : {client.phone}</p>}
        </div>

        <p className="mt-4 text-sm font-semibold">Objet : Prestations de blanchisserie</p>

        <table className="w-full text-sm mt-3 border-collapse">
          <thead><tr className="border-2 border-slate-800 bg-slate-100">
            <th className="px-3 py-2 border border-slate-400 text-left">Date</th>
            <th className="px-3 py-2 border border-slate-400 text-left">Service</th>
            <th className="px-3 py-2 border border-slate-400 text-right">Quantité (kg)</th>
            <th className="px-3 py-2 border border-slate-400 text-right">PU</th>
            <th className="px-3 py-2 border border-slate-400 text-right">Montant ({cur})</th>
          </tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td className="px-3 py-1.5 border border-slate-300">{fmtDay(o.date)}</td>
                <td className="px-3 py-1.5 border border-slate-300">{o.department || "—"}</td>
                <td className="px-3 py-1.5 border border-slate-300 text-right">{kg(o.quantity_kg)}</td>
                <td className="px-3 py-1.5 border border-slate-300 text-right">{fmtMoney(o.unit_price, cur)}</td>
                <td className="px-3 py-1.5 border border-slate-300 text-right">{fmtMoney(o.amount, cur)}</td>
              </tr>
            ))}
            <tr className="font-bold bg-slate-100 border-2 border-slate-800">
              <td className="px-3 py-2 border border-slate-400" colSpan={2}>TOTAL</td>
              <td className="px-3 py-2 border border-slate-400 text-right">{kg(inv.total_kg)}</td>
              <td className="px-3 py-2 border border-slate-400"></td>
              <td className="px-3 py-2 border border-slate-400 text-right">{fmtMoney(inv.total_amount, cur)}</td>
            </tr>
          </tbody>
        </table>

        <p className="mt-4 text-sm">Arrêté la présente facture à la somme de : <span className="font-semibold">{words}</span> ({fmtMoney(inv.total_amount, cur)}).</p>

        {client?.payment_terms && <p className="mt-3 text-sm"><span className="font-semibold">Conditions de règlement :</span> {client.payment_terms}</p>}
        {inv.observation && <p className="mt-1 text-sm"><span className="font-semibold">Observations :</span> {inv.observation}</p>}

        <div className="mt-6 flex justify-end">
          <table className="text-sm">
            <tbody>
              <tr><td className="pr-6 py-0.5 text-slate-600">Montant total</td><td className="text-right font-semibold">{fmtMoney(inv.total_amount, cur)}</td></tr>
              <tr><td className="pr-6 py-0.5 text-slate-600">Payé</td><td className="text-right font-semibold">{fmtMoney(inv.amount_paid, cur)}</td></tr>
              <tr className="border-t border-slate-400"><td className="pr-6 py-0.5 text-slate-600">Solde restant</td><td className="text-right font-bold">{fmtMoney(inv.balance, cur)}</td></tr>
              <tr><td className="pr-6 py-0.5 text-slate-600">Statut</td><td className="text-right font-semibold">{inv.status}</td></tr>
            </tbody>
          </table>
        </div>

        {s?.receipt_footer && <p className="mt-8 text-center text-xs text-slate-400 border-t border-slate-200 pt-3">{s.receipt_footer}</p>}
      </div>

      {/* Historique paiements */}
      <Card className="no-print p-4 max-w-3xl mx-auto">
        <p className="font-semibold text-slate-700 mb-2">Historique des paiements</p>
        {payments.length === 0 ? <p className="text-slate-400 text-sm">Aucun paiement enregistré</p> : (
          <div className="space-y-1.5">
            {payments.map((p) => (
              <div key={p.id} data-testid={`pro-payment-${p.id}`} className="flex justify-between text-sm border-b border-slate-100 py-1.5">
                <span>{fmtDay(p.date)} · {p.method}{p.reference ? ` · Réf ${p.reference}` : ""}</span>
                <span className="font-semibold text-emerald-600">{fmtMoney(p.amount, cur)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!pay} onOpenChange={(v) => !v && setPay(null)}>
        <DialogContent data-testid="pro-payment-dialog">
          <DialogHeader><DialogTitle>Enregistrer un paiement</DialogTitle></DialogHeader>
          {pay && (
            <div className="space-y-3">
              <div><label className="text-sm text-slate-600">Montant ({cur})</label><Input type="number" data-testid="pay-amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
              <div><label className="text-sm text-slate-600">Mode de paiement</label>
                <select data-testid="pay-method" className={selCls} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                  {PAY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div><label className="text-sm text-slate-600">Référence (n° chèque, facultatif)</label><Input data-testid="pay-ref" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
              <div><label className="text-sm text-slate-600">Date</label><input type="date" data-testid="pay-date" className={selCls} value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPay(null)}>Annuler</Button><Button data-testid="save-payment" onClick={savePayment} className="bg-emerald-600 hover:bg-emerald-700">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
